import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { PromosContext } from '../services/context/PromosContext.js';
import useApis from '../services/hooks/useApis.js';
import { BearerTokenContext } from '../services/context/BearerTokenContext.js';
import { ConfigContext } from '../services/context/ConfigContext.js';
import { CategoriesCompleteContext, CategoriesContext } from '../services/context/CategoriesContext.js';
import { getVoucherVendor } from '../services/ReverseHierarchySearch.js';
import Slider from '../components/Slider/Slider.jsx';
import ProductVoucher from '../components/ProductVoucher/ProductVoucher.jsx';
import AddToCartButtons from '../components/AddToCart/AddToCartButtons.jsx';
import useVoucherProcessor from '../services/hooks/useVoucherProcessor.js';
import { SubscriptionDetailsContext } from '../services/context/SubscriptionDetailsContext.js';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../services/Constants/GlobalRoutes.jsx';
import { UserDetailsContext } from '../services/context/UserDetailsContext.js';
import { CampaignContext } from '../services/context/CampaignContext.js';
import moment from 'moment/moment.js';
import { defaultAnalyticsVariables, events, pagePrefix } from '../services/Constants/Analytics.js';
import useNormalizedSubscriptionStatus from '../services/utils/subscriptionUtils.js';

/**
 * @param {object} props - The props for the controller
 * @param {React.ReactNode} props.slot - The content to display within this controller
 * @returns {JSX.Element} The PromosController Component
 */
export default function PromosController({ slot }) {
  const [carouselItems, setCarouselItems] = useState([]);
  const [currentPromo, setCurrentPromo] = useState(null);
  const [promos, setPromos] = useState(/** @type {Array<{promotionTagLabel: string, id: string, productRanking: number}>} */[]);
  const [numPromos, setNumPromos] = useState(1); // Assume at least one promo at all time
  const pageSize = 5;
  const bearerToken = useContext(BearerTokenContext);
  const config = useContext(ConfigContext);
  const categories = useContext(CategoriesContext);
  const categoriesComplete = useContext(CategoriesCompleteContext);
  const { getBuyNowPercentValue } = useVoucherProcessor();
  const subscriptionDetails = useContext(SubscriptionDetailsContext);
  const { getPromos } = useApis();
  const navigateTo = useNavigate();
  const isSubscribed = subscriptionDetails?.isSubscribed;
  const showButtonText = 'Join the Club';
  const userDetails = useContext(UserDetailsContext);
  const subscriptionStatusForAnalytics = useNormalizedSubscriptionStatus();
  const campaignIDs = useContext(CampaignContext);
  const [hideHotDeals, sethideHotDeals] = useState(true);
  let campaignVouchers = null;

  const onClickAnalytics = () => {
    window.utag?.link({
      ...defaultAnalyticsVariables,
      page_name: `${pagePrefix}: landing`,
      link_id: `${pagePrefix}: banner: hot deals just for you`,
      visitor_addon_type_active: `${defaultAnalyticsVariables.app_name}: subscription: ${subscriptionStatusForAnalytics}`,
      event_name: [events.interaction],
    });
  };

  useEffect(() => {
    if (!(config && bearerToken)) return;
    if (promos.length < numPromos) {
      const currentPage = Math.ceil((promos.length + 1) / pageSize); // pages start at 1
      getPromos(bearerToken, pageSize, currentPage)
        .then(data => {
          setNumPromos(data.totalCount);
          const jointPromos = [
            ...promos,
            ...data.items,
          ];
          const promoIds = new Set(jointPromos.map(promo => promo.id));
          setPromos([...promoIds].map(id => jointPromos.find(promo => promo.id === id)));
        });
    }

    if (categories && categoriesComplete) {
      const allVouchers = categories.reduce((mergedVendors, category) => [
        ...mergedVendors,
        ...category.vendors,
      ], []).reduce((mergedVouchers, nextVendor) => {
        const vouchersWithVendorInfo = nextVendor.vouchers.map(v => ({
          ...v,
          vendorName: nextVendor.name,
          vendorIconUrl: nextVendor.iconUrl,
        }));
        return [
          ...mergedVouchers,
          ...vouchersWithVendorInfo,
        ];
      }, []);
      campaignVouchers = [];
      campaignVouchers = allVouchers.filter(voucher => voucher.campaign && (campaignIDs?.length === 0
        || campaignIDs?.includes((voucher.campaign).toString())));
      campaignVouchers = campaignVouchers.filter(voucher => moment().isBetween(voucher.campaignStartDate, voucher.campaignEndDate));
      if (campaignIDs?.length > 0 && campaignVouchers?.length > 0) {
        sethideHotDeals(false);
      }
    }
  }, [
    config,
    bearerToken,
    numPromos,
    promos,
    categories,
    categoriesComplete,
  ]);

  useEffect(() => {
    if (!(categories && promos && categoriesComplete)) return;

    const orderedPromos = promos.toSorted((a, b) => Number(a.productRanking) - Number(b.productRanking));
    const vendors = categories.flatMap(category => category.vendors);
    const vouchers = vendors.flatMap(vendor => vendor.vouchers ?? []);

    const getPromoVouchers = () => orderedPromos
      .map(promo => ({
        promo,
        voucher: vouchers.find(v => v.id === promo.id),
      }))
      .filter(({ voucher }) => voucher)
      .filter(({ voucher }) => isSubscribed || voucher?.promotionTag?.toString() !== config.frontend.general.subscriptionPromotionTag.id);

    const getComingSoon = () => vendors
      .filter(vendor => (vendor.vouchers?.length ?? 0) === 0)
      .map(vendor => ({
        backgroundUrl: vendor.backgroundUrl,
        iconUrl: vendor.iconUrl,
        altText: vendor.name,
        headerText: 'Coming soon.',
      }));

    const getPromoItems = promoVouchers => promoVouchers.map(({ promo, voucher }) => {
      const extraValue = Math.floor(getBuyNowPercentValue(voucher));
      const vendor = getVoucherVendor(categories, voucher);

      return {
        backgroundUrl: voucher.productUrl,
        iconUrl: vendor.iconUrl,
        altText: vendor.name,
        headerText: extraValue ? `${extraValue}% More` : promo.promotionTagLabel,
        subText: extraValue ? promo.promotionTagLabel : undefined,
        actionItem: <button
          className='secondary-button'
          onClick={ () => setCurrentPromo(voucher) }
        >View Voucher</button>,
      };
    });

    const getHotDealsCarousel = () => (userDetails?.vsp === true && !hideHotDeals
      ? [
        {
          backgroundUrl: config.frontend.general.hotDealsCarouselBannerUrl,
          headerText: 'Hot deals just for you',
          subText: 'Today only',
          actionItem: (
            <button
              className='secondary-button'
              onClick={
                () => {
                  navigateTo(ROUTES.HOT_DEALS.get(campaignIDs));
                  onClickAnalytics();
                }
              }
            >
              View offers
            </button>
          ),
        },
      ]
      : []);

    const getSubscriptionCarousel = () => (userDetails.vsp === true
      && !subscriptionDetails?.isSubscribed
      && subscriptionDetails?.isEligibleToSubscribe
      ? [
        {
          backgroundUrl: config.frontend.general.subscriptionCarouselBannerUrl,
          headerText: 'UP TO 50% EXTRA VALUE',
          subText: 'Exclusive deals & vouchers from top brands!',
          actionItem: (
            <button
              className='secondary-button'
              onClick={ () => navigateTo(ROUTES.VODAPAY_CLUB) }
            >
              {showButtonText}
            </button>
          ),
        },
      ]
      : []);
    const promoVouchers = getPromoVouchers();
    const promoItems = getPromoItems(promoVouchers);
    const comingSoon = getComingSoon();
    const hotDealsCarousel = getHotDealsCarousel();
    const subscriptionCarousel = getSubscriptionCarousel();

    setCarouselItems([
      ...promoItems,
      ...comingSoon,
      ...hotDealsCarousel,
      ...subscriptionCarousel,
    ]);
  }, [
    categories,
    promos,
    categoriesComplete,
    subscriptionDetails,
  ]);

  return (
    <PromosContext.Provider value={ carouselItems }>
      { slot }
      {
        currentPromo
          ? (
            <Slider
              onClickOutside={ () => setCurrentPromo(null) }
              slot={
                <ProductVoucher
                  voucher={ currentPromo }
                  actionItem={
                    <AddToCartButtons
                      voucher={ currentPromo }
                    />
                  }
                  descriptionType='buyNow'
                />
              }
            />)
          : null
      }
    </PromosContext.Provider>
  );
}

PromosController.propTypes = { slot: PropTypes.element };
